import {
    RouteLocationNormalized,
    createRouter,
    createWebHistory,
    NavigationGuardNext,
    RouteRecordRaw
} from "vue-router"
import { RecentPage } from "@/models/various/home"
import { PATHS_WITHOUT_AUTH_REGEX } from "@/constants/core"
import globalRoutes from "@/router/globalRoutes"
import companiesAndTerritoriesRoutes from "@/router/companiesAndTerritories"
import otherRoutes from "@/router/other"

const Homepage = () => import("@/views/Homepage.vue")

/* Login */
import login from "@/router/loginRoutes"

/* User */
import user from "@/router/user"

/* CRM */
import batchMessage from "@/router/crm/batchMessage"
import batchNotif from "@/router/crm/batchNotif"
import userFinder from "@/router/crm/crmUserFinder"
import remoteConfig from "@/router/crm/remoteConfig"
import transitPassValidation from "@/router/crm/transitPassValidation"
import challenge from "@/router/crm/challenge"
import SuspiciousCheck from "@/router/crm/suspiciousCheck"
import gifts from "@/router/crm/gifts"

/* Match */
import match from "@/router/match/match"

/* Various */
import userChatroom from "@/router/various/userChatroom"
import pricingAreas from "@/router/various/pricingAreas"

/* Vehicles */
import vehicles from "@/router/vehicles"
import { useHeadStore } from "@/store/core/head"
import { useIndexStore } from "@/store/core/index"

export const routes: RouteRecordRaw[] = [
    {
        path: "/",
        redirect: "/admin/"
    },
    {
        path: "/admin/",
        component: Homepage,
        name: "homePage",
        beforeEnter: () => {
            const store = useHeadStore()
            store.setTitle("")
        }
    },
    ...globalRoutes,
    ...companiesAndTerritoriesRoutes,
    ...otherRoutes,
    ...user,
    ...batchMessage,
    ...userFinder,
    ...batchNotif,
    ...remoteConfig,
    ...transitPassValidation,
    ...challenge,
    ...userChatroom,
    ...match,
    ...pricingAreas,
    ...SuspiciousCheck,
    ...gifts,
    ...vehicles,
    ...login,
    {
        path: "/:pathMatch(.*)*",
        redirect: "/admin/"
    }
]

export const router = createRouter({
    history: createWebHistory(),
    routes
})

router.beforeResolve((to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    const headStore = useHeadStore()
    const indexStore = useIndexStore()
    const title = to.meta.label as string | null | undefined
    const description = to.meta.description as string | null | undefined
    title && headStore.setTitle(title)
    indexStore.pageDescription = description ?? ""
    indexStore.isFromUserFinder = from.name === "userFinder"
    updateRecentPagesForHomepage(title, to)

    const authToken =
        localStorage.getItem(import.meta.env.VITE_AUTH_TOKEN_LABEL) ??
        localStorage.getItem(import.meta.env.VITE_AUTH_TOKEN_LABEL_TO_DEPRECATE)
    if (!PATHS_WITHOUT_AUTH_REGEX.test(to.fullPath) && !authToken) {
        window.location.href = "/admin/login"
    } else {
        next()
    }
})
const updateRecentPagesForHomepage = (name: string | null | undefined, to: RouteLocationNormalized): void => {
    if (name && to.name) {
        const recentPagesStr = localStorage.getItem("recentPages")
        let recentPages: RecentPage[] = recentPagesStr ? JSON.parse(recentPagesStr) : []
        recentPages = recentPages.filter(recentPage => recentPage.label !== name)
        recentPages.unshift({
            label: name,
            routeName: to.name as string,
            path: to.path,
            routeParams: to.params
        })
        recentPages = recentPages.slice(0, 4)
        localStorage.setItem("recentPages", JSON.stringify(recentPages))
    }
}

router.onError((error: Error, to: RouteLocationNormalized) => {
    if (error.message.includes("Failed to fetch dynamically imported module")) {
        window.location.href = to.fullPath // Trigger a Hard Reload on import error.
    }
})
